import { useEffect, useState } from "react";
import { GALLERY_SOURCES, PHOTOS, PRICING_PLANS, SITE, TRAINERS, TRANSLATIONS } from "@/data/content";
import { ADMIN_DATA_KEY, ADMIN_EVENT, syncPublicContent } from "@/lib/publicContentSync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const resolveAssetUrl = (value) => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  if (raw.startsWith("/uploads/")) {
    const apiBase = BACKEND_URL.replace(/\/+$/, "");
    const backendBase = apiBase.replace(/\/api$/i, "");
    if (backendBase) return `${backendBase}${raw}`;
  }
  return raw;
};

const withAssetVersion = (value, version) => {
  const url = resolveAssetUrl(value);
  const v = typeof version === "string" ? version.trim() : "";
  if (!url || !v || url.startsWith("data:")) return url;
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}v=${encodeURIComponent(v)}`;
};

const readAdminData = () => {
  try {
    const raw = localStorage.getItem(ADMIN_DATA_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getGalleryFallbackLabel = (lang, idx) => {
  const sourceLang = lang === "ru" ? "ua" : lang;
  const labels = TRANSLATIONS?.[sourceLang]?.gallery?.images || TRANSLATIONS?.ro?.gallery?.images || [];
  return (labels[idx] || "").trim();
};

const isDefaultGalleryLabel = (value, idx) => {
  const raw = (value || "").trim();
  if (!raw) return false;
  return ["ro", "en", "ua"].some((lang) => raw === getGalleryFallbackLabel(lang, idx));
};

const resolveTranslation = (translations, lang, fallback = {}) => {
  if (!translations || typeof translations !== "object") return fallback;
  if (lang === "ua") {
    return translations.ua || translations.ru || translations.ro || translations.en || fallback;
  }
  if (lang === "ru") {
    return translations.ru || translations.ua || translations.ro || translations.en || fallback;
  }
  return translations[lang] || translations.ro || translations.en || fallback;
};

const normalizeTrainerSocialLinks = (item) => {
  const source = item?.socialLinks && typeof item.socialLinks === "object" ? item.socialLinks : {};
  const instagram = (
    source.instagram ||
    source.instagramUrl ||
    item?.instagram ||
    item?.instagramUrl ||
    ""
  ).trim();
  const facebook = (
    source.facebook ||
    source.facebookUrl ||
    item?.facebook ||
    item?.facebookUrl ||
    ""
  ).trim();
  return {
    instagram,
    facebook,
    showInstagram: source.showInstagram !== false,
    showFacebook: source.showFacebook !== false,
  };
};

const DEFAULT_PLAN_SLUGS = ["individual", "sala", "clase", "perioada", "copii"].flatMap(
  (group) => (PRICING_PLANS[group] || []).map((plan) => plan.id),
);

const resolvePlanSlug = (item, idx) => {
  const candidates = [item?.slug, item?.planId, item?.planKey, item?.key, item?.sourceId, item?.id];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const raw = candidate.trim();
    if (!raw) continue;
    if (raw.startsWith("default-plan-")) return raw.replace("default-plan-", "");
    if (DEFAULT_PLAN_SLUGS.includes(raw)) return raw;
  }
  const orderIndex = Number(item?.order);
  const fallbackIndex = Number.isFinite(orderIndex) ? orderIndex : idx;
  return DEFAULT_PLAN_SLUGS[fallbackIndex] || "";
};

export const useAdminContent = (lang = "ro") => {
  const [adminData, setAdminData] = useState(() => readAdminData());
  const assetVersion = typeof adminData?.dataVersion === "string" ? adminData.dataVersion : "";

  useEffect(() => {
    const refresh = () => setAdminData(readAdminData());
    window.addEventListener("storage", refresh);
    window.addEventListener(ADMIN_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(ADMIN_EVENT, refresh);
    };
  }, []);

  // Keep public site content consistent across devices by reading canonical backend data.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let cancelled = false;

    const run = async () => {
      await syncPublicContent();
      if (!cancelled) setAdminData(readAdminData());
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const faqItems = (() => {
    const fallback = TRANSLATIONS?.[lang]?.faq?.items || TRANSLATIONS?.ro?.faq?.items || [];
    const rows = Array.isArray(adminData?.faq) ? adminData.faq : [];
    if (!rows.length) return fallback;
    return rows
      .filter((item) => item?.isActive !== false)
      .sort((a, b) => (a?.order || 0) - (b?.order || 0))
      .map((item) => {
        const tr = resolveTranslation(item?.translations, lang, {});
        return {
          q: tr?.question || "",
          a: tr?.answer || "",
        };
      })
      .filter((x) => x.q && x.a);
  })();

  const contacts = (() => {
    const c = adminData?.contacts;
    if (!c || typeof c !== "object") return SITE;
    const socialLinks = c?.socialLinks || {};
    const langKey = lang === "ua" ? "ru" : lang;
    const workingHoursTranslations = c?.workingHoursTranslations || {};
    const baseWorkingHours = c.workingHours || SITE.workingHours;
    return {
      ...SITE,
      phone: c.phone || SITE.phone,
      email: c.email || SITE.email,
      address: c.address || SITE.address,
      mapShareUrl: c.mapUrl || SITE.mapShareUrl,
      social: {
        facebook: socialLinks.facebook || SITE.social.facebook,
        instagram: socialLinks.instagram || SITE.social.instagram,
      },
      socialHandles: {
        facebook: socialLinks.facebookHandle || "@maxfitnavodari",
        instagram: socialLinks.instagramHandle || "@maxfitnavodari",
      },
      workingHours: workingHoursTranslations?.[langKey] || workingHoursTranslations?.ro || baseWorkingHours,
    };
  })();

  const hero = (() => {
    const fallbackDict = TRANSLATIONS?.[lang] || TRANSLATIONS?.ro || {};
    const tr = resolveTranslation(adminData?.hero?.translations, lang, {});
    const rawBg = adminData?.hero?.backgroundImage;
    const safeBg = resolveAssetUrl(rawBg);
    return {
      title: tr.title || SITE.brand,
      titleLead: typeof tr.titleLead === "string" ? tr.titleLead : "",
      titleAccent: typeof tr.titleAccent === "string" ? tr.titleAccent : "",
      titleTail: typeof tr.titleTail === "string" ? tr.titleTail : "",
      subtitle: tr.subtitle || fallbackDict?.hero?.badge || "",
      description: tr.description || fallbackDict?.hero?.description || "",
      buttonText: tr.buttonText || fallbackDict?.hero?.cta1 || "",
      backgroundImage: withAssetVersion(safeBg || PHOTOS.hero, assetVersion),
    };
  })();

  const gallery = (() => {
    const fallbackLabels = TRANSLATIONS?.[lang]?.gallery?.images || TRANSLATIONS?.ro?.gallery?.images || [];
    const langKey = lang === "ua" ? "ru" : lang;
    const rows = Array.isArray(adminData?.gallery) ? adminData.gallery : [];
    if (!rows.length) {
      return GALLERY_SOURCES.map((src, idx) => ({
        id: `default-gallery-${idx + 1}`,
        image: src,
        title: fallbackLabels[idx] || `Image ${idx + 1}`,
      }));
    }
    return rows
      .sort((a, b) => (a?.order || 0) - (b?.order || 0))
      .map((item, idx) => {
        const tr = item?.translations || {};
        const ro = (tr?.ro || item?.title || "").trim();
        const activeText = (tr?.[langKey] || "").trim();
        const useFallbackForLang = (!activeText || (activeText === ro && isDefaultGalleryLabel(ro, idx)));
        const fallbackLang = getGalleryFallbackLabel(langKey, idx);
        return {
          id: item.id || `gallery-${idx + 1}`,
          image: withAssetVersion(item.image, assetVersion),
          title: (useFallbackForLang ? fallbackLang : activeText) || ro || fallbackLabels[idx] || `Image ${idx + 1}`,
        };
      })
      .filter((x) => x.image);
  })();

  const trainers = (() => {
    const rows = Array.isArray(adminData?.trainers) ? adminData.trainers : [];
    const fallbackRows = (TRAINERS || []).map((t, idx) => ({
      id: t.id,
      photo: t.photo,
      isActive: true,
      showAboutButton: true,
      showAboutContent: true,
      socialLinks: {
        instagram: "",
        facebook: "",
        showInstagram: true,
        showFacebook: true,
      },
      order: idx,
      translations: {
        ro: {
          name: TRANSLATIONS?.ro?.trainers?.items?.[t.id]?.name || t.id,
          role: TRANSLATIONS?.ro?.trainers?.items?.[t.id]?.role || "",
          about: "",
        },
        en: {
          name: TRANSLATIONS?.en?.trainers?.items?.[t.id]?.name || t.id,
          role: TRANSLATIONS?.en?.trainers?.items?.[t.id]?.role || "",
          about: "",
        },
        ru: {
          name: TRANSLATIONS?.ua?.trainers?.items?.[t.id]?.name || t.id,
          role: TRANSLATIONS?.ua?.trainers?.items?.[t.id]?.role || "",
          about: "",
        },
      },
    }));
    const source = rows.length ? rows : fallbackRows;
    return source
      .filter((item) => item?.isActive !== false)
      .sort((a, b) => (a?.order || 0) - (b?.order || 0))
      .map((item) => {
        const tr = resolveTranslation(item?.translations, lang, {});
        const socialLinks = normalizeTrainerSocialLinks(item);
        return {
          id: item.id,
          photo: withAssetVersion(item.photo, assetVersion),
          name: tr?.name || "",
          role: tr?.role || "",
          about: tr?.about || "",
          showAboutButton: item?.showAboutButton !== false,
          showAboutContent: item?.showAboutContent !== false,
          socialLinks,
        };
      });
  })();

  const plansById = (() => {
    const rows = Array.isArray(adminData?.plans) ? adminData.plans : [];
    const getTr = (item) => resolveTranslation(item?.translations, lang, {});

    return rows.reduce((acc, item, idx) => {
      const baseId = resolvePlanSlug(item, idx);
      if (!baseId) return acc;
      const tr = getTr(item);
      const features = Array.isArray(tr?.features) ? tr.features : [];
      acc[baseId] = {
        id: baseId,
        slug: baseId,
        price: Number(item?.price ?? 0),
        periodValue: Number(item?.periodValue ?? 1),
        periodUnit: item?.periodUnit || "month",
        isActive: item?.isActive !== false,
        order: Number(item?.order ?? 0),
        name: tr?.name || "",
        description: tr?.description || "",
        buttonText: tr?.buttonText || "",
        features,
      };
      return acc;
    }, {});
  })();

  return { faqItems, contacts, hero, gallery, trainers, plansById };
};
